// src/pages/notifications/ContactEditPage.tsx
// Contact edit — settings-style FormRow layout (label-left), one atomic Save.

import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useNotificationsContact, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import { FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { validateContactForm, formatContactType } from '@/features/notifications/logic';
import { ContactTypeValues } from '@core/types/notifications/ContactType';
import type { Contact } from '@core/types/notifications/Contact';
import type { ContactFormState } from '@/features/notifications/types';

export const ContactEditPage = observer(() => {
  const store = useNotificationsContact();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<ContactFormState>({ type: 'CONTACT_TYPE_UNSPECIFIED', value: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { if (id) store.GetContact(id); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const c = store.currentItem as Contact | null;
    if (c && !loaded) {
      setForm({ type: c.type ?? 'CONTACT_TYPE_UNSPECIFIED', value: c.value ?? '' });
      setLoaded(true);
    }
  }, [store.currentItem, loaded]);

  const set = (patch: Partial<ContactFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(patch)) delete next[k];
      return next;
    });
  };

  const handleSave = async () => {
    const validation = validateContactForm(form);
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }
    try {
      const current = store.currentItem as Contact;
      await store.UpdateContact(id ?? '', { ...current, type: form.type, value: form.value.trim() } as never);
      setDirty(false);
      navigate(`/settings/contacts/${id}`);
    } catch (err) {
      setFormError(err);
    }
  };

  if (store.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/settings/contacts/${id}`} className="text-sm text-primary hover:text-primary/80">{t('common.back')}</Link>
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('notifications.editContact')}</h1>
      </div>

      {formError != null && (
        <ErrorAlert error={formError} dismissible onDismiss={() => setFormError(null)} />
      )}

      <Card>
        <CardHeader><CardTitle>{t('notifications.contactDetails')}</CardTitle></CardHeader>
        <CardContent>
          <FormRow label={t('notifications.contactType')}>
            <Select value={form.type} onChange={(e) => set({ type: e.target.value })} fullWidth={false} className="w-48">
              {ContactTypeValues.filter((v) => v !== 'CONTACT_TYPE_UNSPECIFIED').map((s) => {
                const ct = formatContactType(s);
                return <option key={s} value={s}>{t(ct.labelKey)}</option>;
              })}
            </Select>
          </FormRow>
          <FormRow label={t('notifications.contactValue')} htmlFor="contact-edit-value">
            <input
              id="contact-edit-value"
              value={form.value}
              onChange={(e) => set({ value: e.target.value })}
              autoCapitalize="off"
              className="w-64 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.value && <p className="mt-1 text-sm text-destructive">{errors.value}</p>}
          </FormRow>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} isLoading={store.isSaving} disabled={!dirty}>{t('common.saveChanges')}</Button>
      </div>
    </div>
  );
});

export default ContactEditPage;
